import types from '../../mutation-types';
import KanbanBoardsAPI from '../../../api/kanban/boards';

const LAST_BOARD_STORAGE_KEY = 'kanban_last_board_id';

export const state = {
  records: [],
  currentBoardId: null,
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getBoards: _state => _state.records,
  getCurrentBoardId: _state => _state.currentBoardId,
  getCurrentBoard: _state =>
    _state.records.find(b => b.id === _state.currentBoardId) || null,
  getBoardById: _state => id =>
    _state.records.find(b => b.id === Number(id)) || null,
  getUIFlags: _state => _state.uiFlags,
};

export const actions = {
  fetch: async ({ commit, dispatch, state: s }) => {
    commit(types.SET_KANBAN_BOARDS_UI_FLAG, { isFetching: true });
    try {
      const response = await KanbanBoardsAPI.get();
      const boards = response.data || [];
      commit(types.SET_KANBAN_BOARDS, boards);

      if (boards.length === 0) {
        commit(types.SET_KANBAN_CURRENT_BOARD_ID, null);
        return;
      }

      const stored = Number(localStorage.getItem(LAST_BOARD_STORAGE_KEY));
      const candidate = boards.find(b => b.id === stored) || boards[0];
      if (s.currentBoardId !== candidate.id) {
        dispatch('setCurrentBoard', candidate.id);
      }
    } finally {
      commit(types.SET_KANBAN_BOARDS_UI_FLAG, { isFetching: false });
    }
  },

  setCurrentBoard: ({ commit, dispatch }, boardId) => {
    commit(types.SET_KANBAN_CURRENT_BOARD_ID, boardId);
    if (boardId) {
      localStorage.setItem(LAST_BOARD_STORAGE_KEY, String(boardId));
      dispatch('kanban/tasks/fetch', boardId, { root: true });
    }
  },

  fetchOne: async ({ commit }, id) => {
    const response = await KanbanBoardsAPI.show(id);
    commit(types.UPDATE_KANBAN_BOARD, response.data);
    return response.data;
  },

  create: async ({ commit }, payload) => {
    commit(types.SET_KANBAN_BOARDS_UI_FLAG, { isCreating: true });
    try {
      const response = await KanbanBoardsAPI.create(payload);
      commit(types.ADD_KANBAN_BOARD, response.data);
      return response.data;
    } finally {
      commit(types.SET_KANBAN_BOARDS_UI_FLAG, { isCreating: false });
    }
  },

  update: async ({ commit }, { id, ...payload }) => {
    commit(types.SET_KANBAN_BOARDS_UI_FLAG, { isUpdating: true });
    try {
      const response = await KanbanBoardsAPI.update(id, payload);
      commit(types.UPDATE_KANBAN_BOARD, response.data);
      return response.data;
    } finally {
      commit(types.SET_KANBAN_BOARDS_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async ({ commit }, id) => {
    commit(types.SET_KANBAN_BOARDS_UI_FLAG, { isDeleting: true });
    try {
      await KanbanBoardsAPI.delete(id);
      commit(types.DELETE_KANBAN_BOARD, id);
    } finally {
      commit(types.SET_KANBAN_BOARDS_UI_FLAG, { isDeleting: false });
    }
  },
};

export const mutations = {
  [types.SET_KANBAN_BOARDS_UI_FLAG](_state, data) {
    _state.uiFlags = { ..._state.uiFlags, ...data };
  },
  [types.SET_KANBAN_BOARDS](_state, records) {
    _state.records = records;
  },
  [types.SET_KANBAN_CURRENT_BOARD_ID](_state, id) {
    _state.currentBoardId = id;
  },
  [types.ADD_KANBAN_BOARD](_state, board) {
    _state.records.push(board);
  },
  [types.UPDATE_KANBAN_BOARD](_state, board) {
    const idx = _state.records.findIndex(b => b.id === board.id);
    if (idx === -1) _state.records.push(board);
    else _state.records.splice(idx, 1, board);
  },
  [types.DELETE_KANBAN_BOARD](_state, id) {
    _state.records = _state.records.filter(b => b.id !== id);
    if (_state.currentBoardId === id) _state.currentBoardId = null;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
